import { useCallback, useEffect, useRef, useState } from 'react';
import './manager-league.styles.scss';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { listenToDoc } from '@/utils/getDataFromDb';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { unseenGames } from '@shared/replay.js';
import ManagerShell from '@/components/HOME/manager-shell/manager-shell.component';
import Standings from '@/components/SHARED/standings/standings.component';
import ScheduleView from '@/components/SHARED/schedule-view/schedule-view.component';
import LeadersView from '@/components/SHARED/leaders-view/leaders-view.component';
import GameView from '@/components/SHARED/game-view/game-view.component';
import LeagueHistory from '@/components/SHARED/league-history/league-history.component';
import PastSeason from '@/components/SHARED/league-history/past-season.component';
import RecordBook from '@/components/SHARED/record-book/record-book.component';
import PortalView from '@/components/SHARED/portal-view/portal-view.component';
import RecruitingView from '@/components/SHARED/recruiting-view/recruiting-view.component';
import TradeView from '@/components/SHARED/trade-view/trade-view.component';
import FrontOffice from '@/components/SHARED/front-office/front-office.component';
import DraftView from '@/components/SHARED/draft-view/draft-view.component';
import { portalVisible } from '@shared/portal.js';
import PlayerView from '@/components/SHARED/player-view/player-view.component';
import RankingsView from '@/components/SHARED/rankings-view/rankings-view.component';
import { Top25Context, top25From } from '@/utils/top25';
import { ShowManagersContext } from '@/utils/managers';
import LeagueManagers from '@/components/HOME/league-managers/league-managers.component';
import NewsView from '@/components/SHARED/news-view/news-view.component';
import { leagueTheme } from '@/utils/theme';
import { teamThemeVars } from '@/utils/teamTheme';
import { useResumeKey } from '@/utils/useResumeKey';
import PostseasonView from '@/components/SHARED/postseason-view/postseason-view.component';
import TeamSchedule from '@/components/SHARED/team-schedule/team-schedule.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import MyTeam from '@/components/HOME/my-team/my-team.component';
import LineupEditor from '@/components/HOME/lineup-editor/lineup-editor.component';
import RosterPage from '@/components/HOME/roster-page/roster-page.component';
import StrategyPanel from '@/components/HOME/strategy-panel/strategy-panel.component';
import TeamNav from '@/components/HOME/team-nav/team-nav.component';
import TeamPage from '@/components/HOME/team-page/team-page.component';
import ResultsGate from '@/components/HOME/results-gate/results-gate.component';
import WatchGame from '@/components/HOME/watch-game/watch-game.component';

// A manager's area in one league: their team, roster, lineup, strategy,
// and the league's
// schedule, standings, leaders, team pages and games.
const ManagerLeague = ({ onOpen }) => {

    const { leagueId } = useParams();
    const userProfile = useSelector(state => state.userData.userProfile);
    const [ league, setLeague ] = useState(undefined);
    const [ teams, setTeams ] = useState(null);
    const [ schedule, setSchedule ] = useState(null);
    const [ leaders, setLeaders ] = useState(null);
    const [ teamStats, setTeamStats ] = useState(null);
    const [ postseason, setPostseason ] = useState(null);
    const [ rankings, setRankings ] = useState(null);
    const [ rankHistory, setRankHistory ] = useState(null);
    const [ myTeam, setMyTeam ] = useState(undefined);
    // users/{appId}: which of the team's games the manager has seen, and
    // whether to ask before showing new results (shared/replay.js).
    const [ userDoc, setUserDoc ] = useState(undefined);
    // Seen on this visit, before the profile catches up: {year, day}.
    const [ seenNow, setSeenNow ] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();

    const myTeamId = league?.managers?.[userProfile?.appId];
    const appId = userProfile?.appId;
    // Fresh listeners when the app comes back from the background, so a
    // game day played meanwhile shows up (and brings up the results gate).
    const resumeKey = useResumeKey();
    // The work page (Front office, Recruiting, Trades, Draft) the manager
    // was on when new results came in: only that page is spared the
    // results gate, and any navigation from it brings the gate up. '' when
    // the results came in elsewhere; null with nothing new.
    const excusedPath = useRef(null);

    useEffect(() => {
        const listeners = [];
        const listen = (path, callback) => listenToDoc({
            path, callback, 'handleListener': (unsub) => listeners.push(unsub),
        });
        listen(`leagues/${leagueId}`, setLeague);
        listen(`leagues/${leagueId}/summary/teams`, (d) => setTeams(d?.teams ?? null));
        listen(`leagues/${leagueId}/summary/schedule`, setSchedule);
        listen(`leagues/${leagueId}/summary/leaders`, setLeaders);
        listen(`leagues/${leagueId}/summary/teamStats`, setTeamStats);
        listen(`leagues/${leagueId}/summary/postseason`, setPostseason);
        listen(`leagues/${leagueId}/summary/rankings`, setRankings);
        listen(`leagues/${leagueId}/summary/rankHistory`, setRankHistory);
        return () => listeners.forEach(unsub => unsub());
    }, [leagueId, resumeKey])

    useEffect(() => {
        setMyTeam(undefined);
    }, [leagueId, myTeamId])

    useEffect(() => {
        if (!myTeamId) return;
        let unsub;
        listenToDoc({
            'path': `leagues/${leagueId}/teams/${myTeamId}`,
            'callback': setMyTeam,
            'handleListener': (u) => { unsub = u; },
        });
        return () => { if (unsub) unsub(); }
    }, [leagueId, myTeamId, resumeKey])

    useEffect(() => {
        if (myTeamId && onOpen) onOpen(leagueId);
    }, [leagueId, myTeamId, onOpen])

    useEffect(() => {
        setUserDoc(undefined);
        setSeenNow(null);
    }, [appId, leagueId])

    useEffect(() => {
        if (!appId) return;
        let unsub;
        listenToDoc({ 'path': `users/${appId}`, 'callback': setUserDoc, 'handleListener': (u) => { unsub = u; } });
        return () => { if (unsub) unsub(); };
    }, [appId, leagueId, resumeKey])

    // Remembers that the manager has seen a game (and every one before it).
    const markSeen = useCallback(async (game) => {
        const year = game.year ?? league?.season?.year ?? 1;
        setSeenNow(s => (!s || s.year < year || (s.year === year && s.day < game.day) ? { year, day: game.day } : s));
        await UseCloudFunction('markGamesSeen', { leagueId, year, day: game.day });
    }, [leagueId, league?.season?.year])

    const loading = <div className='center-col'><ThreeDotsLoader /></div>;
    let content;
    if (league === undefined || !userProfile) {
        content = loading;
    } else if (league === null) {
        content = <div className='g-card'><p>League not found. <Link to='/leagues'>See all leagues</Link></p></div>;
    } else if (!myTeamId) {
        content = (
            <div className='g-card'>
                <h2>{league.name}</h2>
                <p>You don&apos;t manage a team in this league.</p>
                <Link to={`/leagues/${league.path}`} className='g-button primary'>Choose a team</Link>
            </div>
        );
    } else if (!myTeam || userDoc === undefined || !schedule) {
        content = loading;
    } else {
        const base = `/home/${leagueId}`;
        const teamLink = (id) => (id === myTeamId ? base : `${base}/teams/${id}`);
        const gameLink = (id) => `${base}/games/${id}`;
        const playerLink = (teamId, playerId) => `${base}/teams/${teamId}/players/${playerId}`;
        const seasonLink = (year) => `${base}/history/${year}`;
        const periods = league.settings.minutesPerGame >= 48 ? 4 : 2;
        const shared = { league, teams, schedule, teamLink, gameLink, playerLink, seasonLink, rankHistory };
        const watchLink = (id) => `${base}/watch/${id}`;
        // Schedule, Rankings and Postseason mark the teams users manage.
        const withManagers = (el) => <ShowManagersContext.Provider value>{el}</ShowManagersContext.Provider>;

        // New results: ask first (watch or see), unless turned off. The
        // league pages stay hidden behind the question, since they show
        // the results everywhere. Front office, Recruiting, Trades and
        // Draft are work in progress that shouldn't be cut off (and don't
        // show scores), so the page the manager is on gets a note instead
        // until they move on.
        const remembered = userDoc?.seenGames?.[leagueId] ?? null;
        const seen = !seenNow ? remembered : !remembered || remembered.year < seenNow.year ||
            (remembered.year === seenNow.year && remembered.day < seenNow.day) ? seenNow : remembered;
        const askResults = userDoc?.askResults !== false;
        const unseen = unseenGames({ league, schedule, teamId: myTeamId, seen });
        const onWatch = /\/watch\//.test(location.pathname);
        const onUnseenGame = unseen.some(g => location.pathname.endsWith(`/games/${g.id}`));
        const onWorkPage = /\/(office|recruiting|trades|draft)\/?$/.test(location.pathname);
        if (!unseen.length) excusedPath.current = null;
        else if (excusedPath.current === null) excusedPath.current = onWorkPage ? location.pathname : '';
        const working = onWorkPage && excusedPath.current === location.pathname;
        const showResults = async () => {
            const last = unseen[unseen.length - 1];
            await markSeen(last);
            navigate(gameLink(last.id));
        };
        const nextUnseen = (gameId) => {
            const i = unseen.findIndex(g => g.id === gameId);
            return i >= 0 ? unseen[i + 1] ?? null : unseen[0] ?? null;
        };

        content = askResults && unseen.length > 0 && !onWatch && !onUnseenGame && !working ? (
            <ResultsGate
                games={unseen}
                teams={teams}
                teamId={myTeamId}
                onWatch={(g) => navigate(watchLink(g.id))}
                onResults={showResults}
                onStopAsking={async () => {
                    await UseCloudFunction('setResultsPreference', { ask: false });
                    await markSeen(unseen[unseen.length - 1]);
                }} />
        ) : (
            <>
            {askResults && unseen.length > 0 && working &&
                <div className='g-card results-note' role='status'>
                    <span>{unseen.length > 1 ? `${unseen.length} new games are final.` : 'Your game is final.'}</span>
                    <div className='results-note-actions'>
                        <Link to={watchLink(unseen[0].id)} className='g-button primary'>Watch it</Link>
                        <button className='g-button' onClick={showResults}>Show me the result</button>
                    </div>
                </div>
            }
            <Routes>
                <Route path='' element={<MyTeam {...shared} team={myTeam} postseason={postseason} rankings={rankings} askResults={askResults} />}></Route>
                <Route path='watch/:gameId' element={
                    <WatchRoute leagueId={leagueId} league={league} teams={teams} schedule={schedule} rankHistory={rankHistory} teamId={myTeamId} nextUnseen={nextUnseen} onSeen={markSeen} gameLink={gameLink} watchLink={watchLink} base={base} />
                }></Route>
                <Route path='rankings' element={withManagers(<RankingsView league={league} rankings={rankings} teams={teams} teamLink={teamLink} highlightId={myTeamId} />)}></Route>
                <Route path='news' element={<NewsView league={league} teams={teams} teamLink={teamLink} gameLink={gameLink} playerLink={playerLink} highlightId={myTeamId} myConference={myTeam.conference} />}></Route>
                <Route path='managers' element={<LeagueManagers league={league} teams={teams} teamLink={teamLink} highlightId={myTeamId} />}></Route>
                <Route path='postseason' element={withManagers(
                    <PostseasonView league={league} postseason={postseason} teams={teams} teamLink={teamLink} gameLink={gameLink} highlightId={myTeamId}
                        part={league.type === 'pro' ? undefined : 'conference'} />
                )}></Route>
                {league.type !== 'pro' &&
                    <Route path='national' element={withManagers(
                        <PostseasonView league={league} postseason={postseason} teams={teams} teamLink={teamLink} gameLink={gameLink} highlightId={myTeamId} part='national' />
                    )}></Route>
                }
                <Route path='roster' element={<RosterPage league={league} team={myTeam} base={base} playerLink={playerLink} />}></Route>
                <Route path='lineup' element={<LineupEditor league={league} team={myTeam} base={base} />}></Route>
                <Route path='strategy' element={
                    <div className='manager-league'>
                        <TeamNav base={base} />
                        <StrategyPanel league={league} team={myTeam} />
                    </div>
                }></Route>
                <Route path='schedule' element={withManagers(
                    <div className='manager-league'>
                        <div className='g-card'>
                            <h3>{myTeam.name} schedule</h3>
                            <TeamSchedule teamId={myTeamId} schedule={schedule} year={league.season.year} teams={teams} gameLink={gameLink} teamLink={teamLink} />
                        </div>
                        <div className='g-card'>
                            <h3>League schedule</h3>
                            <ScheduleView schedule={schedule} teams={teams} currentDay={league.season.day} gameLink={gameLink} />
                        </div>
                    </div>
                )}></Route>
                <Route path='standings' element={
                    <Standings league={league} teams={teams} teamLink={teamLink} highlightId={myTeamId} />
                }></Route>
                <Route path='leaders' element={<LeadersView leaders={leaders} teamStats={teamStats} teams={teams} teamLink={teamLink} playerLink={playerLink} highlightId={myTeamId} />}></Route>
                <Route path='history' element={<LeagueHistory league={league} />}></Route>
                <Route path='portal' element={<PortalView league={league} teams={teams} teamLink={teamLink} myTeam={myTeam} />}></Route>
                <Route path='recruiting' element={<RecruitingView league={league} teams={teams} teamLink={teamLink} myTeam={myTeam} />}></Route>
                <Route path='trades' element={<TradeView league={league} teams={teams} teamLink={teamLink} myTeam={myTeam} />}></Route>
                <Route path='office' element={<FrontOffice league={league} teams={teams} teamLink={teamLink} myTeam={myTeam} />}></Route>
                <Route path='draft' element={<DraftView league={league} teams={teams} teamLink={teamLink} playerLink={playerLink} myTeam={myTeam} />}></Route>
                <Route path='records' element={<RecordBook league={league} gameLink={gameLink} seasonLink={seasonLink} highlightId={myTeamId} />}></Route>
                <Route path='history/:year' element={<PastSeason league={league} teamLink={teamLink} gameLink={gameLink} />}></Route>
                <Route path='teams/:teamId' element={<TeamPage {...shared} />}></Route>
                <Route path='teams/:teamId/players/:playerId' element={<PlayerRoute leagueId={leagueId} {...shared} />}></Route>
                <Route path='games/:gameId' element={<GameRoute leagueId={leagueId} periods={periods} teamLink={teamLink} playerLink={playerLink} />}></Route>
                <Route path='*' element={<Navigate replace to={base} />}></Route>
            </Routes>
            </>
        );
    }

    return (
        <ManagerShell leagueId={myTeamId ? leagueId : undefined} title={league?.name} rankingsLabel={league?.type === 'pro' ? 'Power Rankings' : 'Rankings'} theme={leagueTheme(league)} themeVars={myTeam ? teamThemeVars(myTeam, { dark: league?.type === 'pro' }) : null} showPortal={portalVisible(league)} showRecruiting={league && league.type !== 'pro'} showOffice={league?.type === 'pro'}>
            <Top25Context.Provider value={top25From(league, rankings)}>
                <div className='manager-league-page'>{content}</div>
            </Top25Context.Provider>
        </ManagerShell>
    )
}

const WatchRoute = ({ nextUnseen, ...props }) => {
    const { gameId } = useParams();
    return <WatchGame {...props} gameId={gameId} next={nextUnseen(gameId)} />;
}

const GameRoute = ({ leagueId, periods, teamLink, playerLink }) => {
    const { gameId } = useParams();
    return <GameView leagueId={leagueId} gameId={gameId} periodsInRegulation={periods} teamLink={teamLink} playerLink={playerLink} />;
}

const PlayerRoute = ({ leagueId, teams, teamLink, gameLink }) => {
    const { teamId, playerId } = useParams();
    return <PlayerView leagueId={leagueId} teamId={teamId} playerId={playerId} teams={teams} teamLink={teamLink} gameLink={gameLink} />;
}

export default ManagerLeague
